/**
 * UTC-based ISO calendar-day helpers ('YYYY-MM-DD'), shared by the scheduling,
 * statistics-filing, and channel-manager day math. All arithmetic is in UTC —
 * a "day" is the instant's UTC calendar date — so it is deterministic and
 * DST-free. Timezone-aware wall-clock projections live in `local-time.ts`.
 */

import { localDateInTimezone } from "./local-time";

/** Milliseconds in a UTC day. Exact: UTC has no DST shifts. */
export const MS_PER_DAY = 86_400_000;

/**
 * The zone a stay's DAY is read in.
 *
 * `reservations.check_in_at` / `check_out_at`, and the copies a work order
 * carries on `tasks`, hold a calendar day stored as midnight UTC. The hour a
 * stay keeps is a separate fact in the wall-clock columns
 * (`properties.check_in_time` / `check_out_time`, overridden per stay by
 * `reservations.check_in_time` / `check_out_time`). A stay day is therefore
 * the instant's UTC date and nothing else: projecting it through the device's
 * or the property's zone lands it on the previous evening anywhere west of
 * Greenwich. Read it with `toIsoDate`, or pass this zone to a formatter.
 */
export const STAY_DAY_ZONE = "UTC";

/** UTC calendar date ('YYYY-MM-DD') of an instant. */
export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** UTC-midnight instant of a 'YYYY-MM-DD' date. */
export function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** 'YYYY-MM-DD' shifted by `days` (may be negative). */
export function addDaysIso(iso: string, days: number): string {
  return toIsoDate(addDaysUtc(parseIsoDate(iso), days));
}

/** New Date `days` UTC days from `date`, time-of-day preserved. */
export function addDaysUtc(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/** Floor an instant to its UTC midnight. */
export function startOfUtcDay(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
}

/**
 * Nights between two instants, counted as calendar days in `timeZone` — so a
 * late checkout or early check-in can't invent a night, and every surface
 * (the tax engine, the host app, the guest portal) reports the same number
 * for the same reservation.
 *
 * For a stay's own columns pass {@link STAY_DAY_ZONE}: they are day markers,
 * and reading them in any other zone can only move a boundary onto the wrong
 * day (a property whose offset crosses zero across a DST switch would count
 * a night that was never sold). Pass the property's timezone only for TRUE
 * instants, where a 17:00 local check-in west of UTC lands past UTC midnight
 * and counting UTC days would drop a night the guest actually stayed.
 * Timezones are stored free text, so a missing or invalid zone falls back to
 * UTC (via `localDateInTimezone`) — never a throw. Same-day is 0 nights;
 * invalid or inverted inputs are 0 as well.
 */
export function nightsBetween(
  checkIn: Date,
  checkOut: Date,
  timeZone?: string | null
): number {
  if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime())) {
    return 0;
  }
  const nights =
    (Date.parse(`${localDateInTimezone(checkOut, timeZone)}T00:00:00Z`) -
      Date.parse(`${localDateInTimezone(checkIn, timeZone)}T00:00:00Z`)) /
    MS_PER_DAY;
  return Math.max(0, nights);
}

const ISO_DATE_PARTS = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A `YYYY-MM-DD` date that exists, as its parts, or null. The shape alone is
 * not enough: `2019-02-31` matches it and works out to an age young enough to
 * earn a tax reduction, so the parts must survive a round trip through UTC.
 */
function readIsoDate(
  iso: string
): { day: number; month: number; year: number } | null {
  const match = ISO_DATE_PARTS.exec(iso.trim());
  if (!match) {
    return null;
  }
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  return isRealDate ? { day, month, year } : null;
}

/**
 * Whole years completed by `on` for someone born on `birthDate`, both
 * `YYYY-MM-DD` calendar dates. Null when either date does not exist or the
 * birth comes after `on`. A 29 February birthday falls on 1 March in a common
 * year, which keeps age bands contiguous.
 */
export function ageOn(birthDate: string, on: string): number | null {
  const birth = readIsoDate(birthDate);
  const day = readIsoDate(on);
  if (!(birth && day)) {
    return null;
  }
  const hasHadBirthday =
    day.month > birth.month ||
    (day.month === birth.month && day.day >= birth.day);
  const age = day.year - birth.year - (hasHadBirthday ? 0 : 1);
  return age >= 0 ? age : null;
}
